import { Component, ElementRef, forwardRef, Input, OnInit, SecurityContext, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { IdeaCustomFieldValue } from 'src/app/core/models/idea.models';
import { clearElement } from 'src/app/core/utils/dom-utils';

@Component({
  selector: 'craft-text-editor',
  templateUrl: './text-editor.component.html',
  styleUrls: ['./text-editor.component.scss'],
  host: {
    '[class.focused]': 'isFocused',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextEditorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class TextEditorComponent implements OnInit, ControlValueAccessor {
  @Input() public maxlength = 10000;
  @Input() public placeholder = 'Search';
  @ViewChild('textEditor', { static: true }) public textEditorRef: ElementRef;

  public onChange = (_: string | IdeaCustomFieldValue) => {};
  public onTouched = () => {};
  public isFocused = false;

  private originValue: IdeaCustomFieldValue | undefined;

  constructor(private domSanitizer: DomSanitizer) {}

  public ngOnInit() {
    document.execCommand('defaultParagraphSeparator', false, 'p');
  }

  public onPaste(e: Event) {
    const event = e as ClipboardEvent;
    if (event.clipboardData) {
      event.preventDefault();
      let content = event.clipboardData.getData('text/plain');
      content = content.replace(/\n/g, '<br/>');
      const sanitizedContent = this.domSanitizer.sanitize(SecurityContext.HTML, content);
      document.execCommand('insertHTML', false, sanitizedContent === null ? undefined : sanitizedContent);
    }
  }

  public onInput() {
    const div = this.inputDiv;
    if (!div) return;

    let html: string = div.innerHTML;
    const text: string = div.innerText.trim();

    if (text === '') {
      html = '';
      clearElement(div);
    }

    if (html.length <= this.maxlength) {
      this.onChange(this.originValue ? { ...this.originValue, value: html } : html);
    }
  }

  public onFocus() {
    this.isFocused = true;
  }

  public onBlur() {
    this.isFocused = false;
    this.onInput();
  }

  public registerOnChange(fn: (_: string | IdeaCustomFieldValue) => void): void {
    this.onChange = fn;
  }

  public writeValue(value: string | IdeaCustomFieldValue): void {
    const div = this.inputDiv;
    if (!div) return;

    if (typeof value === 'string') {
      div.innerHTML = value;
    } else if (value && typeof value === 'object') {
      this.originValue = value;
      div.innerHTML = typeof value.value === 'string' ? value.value : '';
    } else {
      div.innerHTML = '';
    }
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputDiv?.setAttribute('disabled', 'disabled');
    } else {
      this.inputDiv?.removeAttribute('disabled');
    }
  }

  private get inputDiv(): HTMLDivElement | undefined {
    return this.textEditorRef?.nativeElement;
  }
}
